import React, {useMemo} from "react";
import {useEmployeeHierarchyStore} from "../../api/EmployeeHierarchyStore";
import {filterHierarchy} from "../../util/EmployeeHierarchy";
import {CompanyTreemap} from "../company-treemap/CompanyTreemap";
import {CompanyTreemapFilter, companyTreemapFilterFormSerializer} from "../company-treemap/CompanyTreemapFilter";
import {useFilter} from "../../util/FilterSerializer";
import {SeatingPlanModel} from "./SeatingPlanModel";
import {Stack} from '@mui/material';
import {RemoteValue} from "../../util/RemoteValue";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {newNeighbourhoodAction} from "./actions/CreateNewSeatingPlanNeighbourhoodMenuItem";
import {SeatingPlanTab} from "./SeatingPlanTab";
import {useResidenceStore} from "../../api/ResidenceStore";
import {SeatingPlanStickyFilter} from "./SeatingPlanStickyFilter";
import {SeatingPlanTabLabel} from "./SeatingPlanTabLabel";

function CompanyTreemapTab({model}: {
    model: SeatingPlanModel,
}) {
    const filterSerializer = useMemo(
        () => companyTreemapFilterFormSerializer(model.hoodItems),
        [model.hoodItems]
    );
    const [filter, setFilter] = useFilter(filterSerializer);
    const employeeNeighbourhood = useMemo(() => {
        return RemoteValue.fromValue(model.modifiedEmployeeNeighbourhood);
    }, [model.modifiedEmployeeNeighbourhood]);
    const employeeHierarchy = useEmployeeHierarchyStore(employeeNeighbourhood);
    const residencyModel = useResidenceStore().remoteValue;

    const hierarchy = useMemo(() => (
        RemoteValue
            .combine({employeeHierarchy, residencyModel})
            .map(({employeeHierarchy, residencyModel}) => {
                return filterHierarchy(
                    employeeHierarchy.globalHierarchy,
                    node => node.type === 'node' && residencyModel.isResident(node) && !node.deactivated,
                );
            })
    ), [employeeHierarchy, residencyModel]);

    return (
        <Stack spacing={2}>
            <SeatingPlanStickyFilter>
                <CompanyTreemapFilter filter={filter} setFilter={setFilter} allNeighbourhoods={model.hoodItems}/>
            </SeatingPlanStickyFilter>
            <LoadingData
                value={hierarchy}
                defaultErrorMessage="Failed to load employees data. Please try again later."
                emptyMessage="No employees found."
            >{hierarchy =>
                <CompanyTreemap hierarchyData={hierarchy} filter={filter}/>
            }</LoadingData>
        </Stack>
    );
}

export const treemapTab: SeatingPlanTab<'newNeighbourhoodAction'> = {
    label: (_, active) => (
        <SeatingPlanTabLabel label="Treemap" active={active}/>
    ),
    key: 'company-treemap',
    actions: {
        newNeighbourhoodAction,
    },
    content: CompanyTreemapTab,
};
